const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3100/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  // Bound every economy call — a hung connection must not freeze init/commit forever.
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), 8000)
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      signal: ctrl.signal,
    })
  } finally {
    clearTimeout(timer)
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    const err = new Error(body.error || `HTTP ${res.status}`) as Error & { status?: number }
    err.status = res.status // lets callers distinguish stale-id (404) from transient (5xx) failures
    throw err
  }
  return res.json()
}

/** Create an anonymous player. */
export async function createPlayer(nickname?: string): Promise<{ id: string; nickname: string }> {
  return request('/players', {
    method: 'POST',
    body: JSON.stringify({ nickname: nickname || '' }),
  })
}

/** Get leaderboard scores. */
export async function getLeaderboard(limit = 50): Promise<{
  scores: { nickname: string; score: number; level: number; jelly_earned: number }[]
  total: number
}> {
  return request(`/leaderboard?limit=${limit}`)
}

/** Submit a score. */
export async function submitScore(data: {
  player_id: string; nickname: string; score: number; level: number; jelly_earned: number
  /** Stable per-run id → server de-dupes replays so jelly is credited exactly once. */
  client_run_id?: string
}): Promise<{ id: number } | { deduped: true }> {
  // A replayed run returns 200 { deduped: true } (no id); a new run returns 201 { id, ... }.
  return request('/scores', { method: 'POST', body: JSON.stringify(data) })
}

/** Get player progress. */
export async function getProgress(playerId: string): Promise<{
  player_id: string; jelly_balance: number; unlocked_items: string[]
}> {
  return request(`/progress/${playerId}`)
}

/** Update player progress. */
export async function updateProgress(
  playerId: string,
  data: { jelly_balance?: number; unlocked_items?: string[] }
): Promise<{ player_id: string; jelly_balance: number; unlocked_items: string[] }> {
  return request(`/progress/${playerId}`, { method: 'PUT', body: JSON.stringify(data) })
}

/** Get shop catalog. */
export async function getShopItems(): Promise<{
  items: { id: string; name: string; type: string; cost: number; description: string }[]
}> {
  return request('/shop')
}

/** Purchase a shop item. */
export async function purchaseItem(playerId: string, itemId: string): Promise<{
  purchased: string; new_balance: number; unlocked_items: string[]
}> {
  return request('/shop/purchase', {
    method: 'POST',
    body: JSON.stringify({ player_id: playerId, item_id: itemId }),
  })
}

/** Check server health and return the release identity paired with the client. */
export async function checkHealth(): Promise<{ ok: boolean; version?: string }> {
  return request('/health')
}
